/**
 * Network Status Service
 * Monitors online/offline status with lightweight checks
 */

import { create } from 'zustand';

/**
 * Network Status Store
 */
export const useNetworkStatus = create((set) => ({
  isOnline: navigator.onLine,
  lastChecked: new Date(),
  setOnline: (status) => set({ isOnline: status, lastChecked: new Date() }),
}));

/**
 * Initialize network status monitoring
 * Uses multiple detection methods for reliability
 */
export const initializeNetworkMonitor = () => {
  console.log('🌐 Initializing network status monitor...');
  
  // Method 1: Browser online/offline events
  const handleOnline = () => {
    console.log('✅ Network online (browser event)');
    useNetworkStatus.getState().setOnline(true);
  };
  
  const handleOffline = () => {
    console.log('❌ Network offline (browser event)');
    useNetworkStatus.getState().setOnline(false);
  };
  
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  // Method 2: Periodic lightweight check (every 30 seconds)
  // Use a more mobile-friendly approach
  const checkNetworkStatus = async () => {
    // First check: navigator.onLine (instant, but not always accurate)
    if (!navigator.onLine) {
      // If browser says offline, trust it
      if (useNetworkStatus.getState().isOnline) {
        console.log('❌ Network offline (navigator.onLine = false)');
        useNetworkStatus.getState().setOnline(false);
      }
      return;
    }
    
    // If navigator.onLine is true, assume we're online
    // Only do a fetch check if we were previously offline
    const currentStatus = useNetworkStatus.getState().isOnline;
    
    if (currentStatus) {
      // Already online, no need to check - trust navigator.onLine
      return;
    }
    
    // We were offline, try to confirm we're back online with a fetch
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout for mobile
      
      await fetch('https://www.google.com/favicon.ico', {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-cache',
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      // Fetch succeeded, we're back online
      console.log('✅ Network online (fetch check confirmed)');
      useNetworkStatus.getState().setOnline(true);
    } catch (error) {
      // Fetch failed, but navigator.onLine is true
      // On mobile, this could be a false negative (CORS, timeout, etc.)
      // Default to trusting navigator.onLine
      console.log('⚠️ Fetch check failed but navigator.onLine=true, assuming online on mobile');
      useNetworkStatus.getState().setOnline(true);
    }
  };
  
  // Initial check
  const initialStatus = navigator.onLine;
  console.log(`🌐 Initial network status: ${initialStatus ? 'Online' : 'Offline'}`);
  checkNetworkStatus();
  
  // Start periodic checks every 30 seconds - store interval locally
  const checkInterval = setInterval(checkNetworkStatus, 30000);
  console.log('✅ Network status monitor initialized - checking every 30 seconds');
  
  // Return cleanup function
  return () => {
    console.log('🛑 Cleaning up network status monitor');
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    if (checkInterval) {
      clearInterval(checkInterval);
    }
  };
};

/**
 * Get current network status
 * @returns {boolean} True if online
 */
export const isOnline = () => {
  return useNetworkStatus.getState().isOnline;
};

/**
 * Manually trigger network check
 */
export const checkNetworkNow = async () => {
  // Check navigator.onLine first
  if (!navigator.onLine) {
    useNetworkStatus.getState().setOnline(false);
    return false;
  }
  
  // navigator.onLine is true, try fetch to confirm
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout for mobile
    
    await fetch('https://www.google.com/favicon.ico', {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-cache',
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    useNetworkStatus.getState().setOnline(true);
    return true;
  } catch (error) {
    // Fetch failed but navigator.onLine is true
    // On mobile, trust navigator.onLine (fetch can fail for many reasons)
    console.log('⚠️ Fetch check failed but navigator.onLine=true, assuming online');
    useNetworkStatus.getState().setOnline(true);
    return true;
  }
};





